import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconWhatsApp, IconInstagram } from '../Icons/Icons';
import { useSiteLogo } from '../../../context/SiteLogoContext';
import ApiImage from '../ApiImage';
import { BRAND } from '../../../config/brand';
import './Footer.css';

/** Fallback shop links when categories have not loaded yet */
const FALLBACK_SHOP_LINKS = [
  { to: '/shop/women/rings', label: 'Rings' },
  { to: '/shop/women/earrings', label: 'Earrings' },
  { to: '/shop/women/necklaces', label: 'Necklaces' },
  { to: '/shop/women/bracelets', label: 'Bracelets' },
];
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Footer — Mobile-first, SEO internal linking, trust signals.
 * Visibility: see Layout (showFooterFinal) and ProductDetail (always on PDP).
 * Docs: docs/FOOTER_BEHAVIOUR.md.
 *
 * Newsletter: not in footer (homepage section only). Replace WHATSAPP_NUMBER
 * and social hrefs before deploy.
 */

const BRAND_NAME = BRAND?.name || 'Luvia';
const BRAND_YEAR = new Date().getFullYear();

// TODO: Replace with real WhatsApp Business number (format: 91XXXXXXXXXX)
const WHATSAPP_NUMBER  = '91XXXXXXXXXX';
const WHATSAPP_MESSAGE = encodeURIComponent('Hi, I need help with my order');
const WHATSAPP_HREF    = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

const SOCIAL = {
  instagram: BRAND?.instagram || 'https://www.instagram.com/theluviajewels',
};

function Footer() {
  const { resolvedLogoUrl, logoAlt } = useSiteLogo();
  const [shopLinks, setShopLinks] = useState(FALLBACK_SHOP_LINKS);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      try {
        const catRes = await fetch(`${API_URL}/categories/slug/women`, {
          signal,
          cache: 'no-store',
        });
        if (!catRes.ok) return;
        const catJson = await catRes.json();
        if (signal.aborted || !catJson?.success || !catJson?.category?._id) return;

        const subRes = await fetch(
          `${API_URL}/subcategories?categoryId=${encodeURIComponent(catJson.category._id)}&onlyWithProducts=true`,
          { signal, cache: 'no-store' }
        );
        if (!subRes.ok) return;
        const subJson = await subRes.json();
        if (signal.aborted || !subJson?.success || !Array.isArray(subJson.subcategories)) return;

        const links = subJson.subcategories
          .filter((sub) => {
            if (!sub?.name || !sub?.slug) return false;
            if (sub.isDeleted === true || sub.isActive === false) return false;
            if (typeof sub.productCount === 'number' && sub.productCount <= 0) return false;
            return true;
          })
          .map((sub) => ({
            to: `/shop/women/${sub.slug}`,
            label: sub.name,
          }));

        if (links.length > 0) {
          setShopLinks(links);
        }
      } catch {
        // Keep fallback links on failure.
      }
    })();

    return () => controller.abort();
  }, []);

  return (
    <footer className="footer" aria-label="Site footer">

      {/* ── Main Footer Grid ── */}
      <div className="footer__main">
        <div className="footer__grid">

          {/* ── Brand Column ── */}
          <div className="footer__column footer__column--brand">
            <Link to="/" className="footer__logo" aria-label={`${BRAND_NAME} home`}>
              <ApiImage src={resolvedLogoUrl} fallback="/logo.png" alt={logoAlt || BRAND_NAME} className="footer__logo-img" width="220" height="60" />
            </Link>

            <p className="footer__tagline">
              {BRAND?.tagline || "Women's Artificial Jewelry"}
            </p>

            {/*
              WhatsApp — high-trust support signal for e‑commerce.
              Shows customers a real person is available. Replace WHATSAPP_NUMBER before deploy.
            */}
            <a
              href={WHATSAPP_HREF}
              className="footer__whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with us on WhatsApp for order support"
            >
              <IconWhatsApp size={15} />
              WhatsApp Support
            </a>

            <div className="footer__social" role="list" aria-label={`Follow ${BRAND_NAME} on social media`}>
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="footer__social-icon"
                aria-label={`Follow ${BRAND_NAME} on Instagram`}
                role="listitem"
              >
                <IconInstagram size={17} />
              </a>
            </div>
          </div>

          {/* ── Shop Column ── */}
          <nav className="footer__column" aria-label="Shop navigation">
            <p className="footer__col-heading">Shop</p>
            {shopLinks.map(({ to, label }) => (
              <Link key={to} to={to} className="footer__link">{label}</Link>
            ))}
            <Link to="/new-arrivals"             className="footer__link">New Arrivals</Link>
            <Link to="/bestsellers"              className="footer__link">Bestsellers</Link>
          </nav>

          {/* ── Help Column ── */}
          <nav className="footer__column" aria-label="Customer support navigation">
            <p className="footer__col-heading">Help</p>
            <Link to="/shipping"  className="footer__link">Shipping &amp; Delivery</Link>
            <Link to="/returns"   className="footer__link">Returns &amp; Exchanges</Link>
            <Link to="/size-guide" className="footer__link">Size Guide</Link>
            <Link to="/contact"   className="footer__link">Contact Us</Link>
            <Link to="/faq"       className="footer__link">FAQs</Link>
          </nav>

          {/* ── Company Column ── */}
          <nav className="footer__column" aria-label="Company navigation">
            <p className="footer__col-heading">Company</p>
            <Link to="/about"          className="footer__link">About Us</Link>
            <Link to="/story"          className="footer__link">Our Story</Link>
          </nav>
        </div>

        {/* Trust badges — consistent with Cart, Checkout, TrustBar, /returns policy */}
        <div className="footer__trust" aria-label="Shopping guarantees">
          <div className="footer__trust-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>Secure Checkout</span>
          </div>
          <div className="footer__trust-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            </svg>
            <span>Free Shipping Above ₹500</span>
          </div>
          <div className="footer__trust-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            <span>Returns for size &amp; defective only</span>
          </div>
          <div className="footer__trust-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            <span>UPI · Cards · Net Banking</span>
          </div>
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className="footer__bottom">
        <p className="footer__copyright">
          © {BRAND_YEAR} {BRAND_NAME}. All rights reserved.
        </p>
        <nav className="footer__legal" aria-label="Legal">
          <Link to="/privacy"  className="footer__legal-link">Privacy Policy</Link>
          <span className="footer__legal-sep" aria-hidden="true">·</span>
          <Link to="/terms"    className="footer__legal-link">Terms of Service</Link>
          <span className="footer__legal-sep" aria-hidden="true">·</span>
          <Link to="/cookies"  className="footer__legal-link">Cookies</Link>
        </nav>
      </div>

      {/*
        Structured data REMOVED from footer.
        Organization schema is in Home.jsx <Helmet>.
        Having two conflicting Organization schemas (one with wrong brand name)
        sends contradictory signals to Google.
        Rule: structured data goes in <Helmet> in page components, never in footer DOM.
      */}
    </footer>
  );
}

export default Footer;